import { Component, Inject, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { eOrderType, ePartyType, eSummaryEntityType, eSummaryTextBlockType, Order, PpsaSummaryResult, SlideDownAnimation, SummaryEntity } from 'reg-hub-common';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'app-search-summary-result',
    templateUrl: './search-summary-result.component.html',
    styleUrl: './search-summary-result.component.css',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchSummaryResultComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  protected result: PpsaSummaryResult;
  protected order: Order;

  protected serialCollateral!: SummaryEntity[];
  protected serialCollateralFiltered!: SummaryEntity[];
  protected serialCollateralLeft!: SummaryEntity[];
  protected serialCollateralRight!: SummaryEntity[];
  protected serialCollateralPageSizeOptions = [10];

  protected currentPageSize = 10;
  protected currentPageIndex = 0;
  protected serialCollateralLength = 0;

  protected criteria: string = '';

  protected showGeneralCollateral = true;
  protected showAdditionalInformation = true;
  protected showAmendmentDetails = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any) {
    this.result = data.result;
    this.order = data.order;
  }

  ngOnInit() {
    this.serialCollateral = this.getSerialCollateral() ?? [];
    this.serialCollateralFiltered = this.serialCollateral;
    this.serialCollateralLength = this.serialCollateral.length;
    this.serialCollateralLeft = this.serialCollateral.slice(0, 5);
    this.serialCollateralRight = this.serialCollateral.slice(5, 10);

    if (this.serialCollateral.length > 40) {
      this.serialCollateralPageSizeOptions.push(20);
    }

    if (this.serialCollateral.length > 80) {
      this.serialCollateralPageSizeOptions.push(40);
    }

    if (this.order.orderTypeID === eOrderType.assetSearch) {
      this.criteria = this.order.assets?.at(0)?.serialNumber ?? '';
    } else if (this.order.orderTypeID === eOrderType.debtorSearch) {
      const party = this.order.parties?.at(0);

      if (party?.partyTypeID === ePartyType.individualDebtor) {
        this.criteria = `${party.firstName} ${party.middleName} ${party.lastName}`;
      } else {
        this.criteria = party?.busName ?? '';
      }
    }
  }

  getRegistrationPeriod() {
    const from = new Date(this.result.from!);
    const to = new Date(this.result.to!);
    const period = to.getTime() - from.getTime();
    return (period / 31636000000).toFixed(0);
  }

  getRemainingRegistrationPeriod() {
    const to = new Date(this.result.to!);
    const now = new Date();
    const remaining = to.getTime() - now.getTime();

    if (remaining <= 0) {
      return this.transloco.translate('orders.expired');
    } else {
      return (remaining / 31536000000).toFixed(1);
    }
  }

  hasDebtors() {
    return this.result.entities?.some(e => e.entityType === eSummaryEntityType.debtor);
  }

  hasTransferors() {
    return this.result.entities?.some(e => e.entityType === eSummaryEntityType.transferor);
  }

  hasTransferees() {
    return this.result.entities?.some(e => e.entityType === eSummaryEntityType.transferee);
  }

  hasSecuredParties() {
    return this.result.entities?.some(e => e.entityType === eSummaryEntityType.securedParty);
  }

  hasAssignors() {
    return this.result.entities?.some(e => e.entityType === eSummaryEntityType.assignor);
  }

  hasAssignees() {
    return this.result.entities?.some(e => e.entityType === eSummaryEntityType.assignee);
  }

  hasDealers() {
    return this.result.entities?.some(e => e.entityType === eSummaryEntityType.dealer);
  }

  hasOtherParties() {
    return this.result.entities?.some(e => e.entityType === eSummaryEntityType.otherParty);
  }

  hasUndeterminedParties() {
    return this.result.entities?.some(e => e.entityType === eSummaryEntityType.undeterminedParty);
  }

  hasSerialCollateral() {
    return this.result.entities?.some(e => e.entityType === eSummaryEntityType.serialCollateral);
  }

  hasGeneralCollateral() {
    return this.result.textBlocks?.some(t => t.type === eSummaryTextBlockType.generalCollateral);
  }

  hasGeneralCollateralFlags() {
    return this.order.jurisdiction === 'MB' &&
      ((this.result.securityInterest ?? false) || (this.result.securityInterestExceptFor ?? false));
  }

  hasAdditionalInformation() {
    return this.result.textBlocks?.some(t => t.type === eSummaryTextBlockType.additionalInformation);
  }

  hasAmendmentDetails() {
    return this.result.textBlocks?.some(t => t.type === eSummaryTextBlockType.amendmentDetails);
  }

  getDebtors() {
    return this.result.entities?.filter(e => e.entityType === eSummaryEntityType.debtor);
  }

  getTransferors() {
    return this.result.entities?.filter(e => e.entityType === eSummaryEntityType.transferor);
  }

  getTransferees() {
    return this.result.entities?.filter(e => e.entityType === eSummaryEntityType.transferee);
  }

  getSecuredParties() {
    return this.result.entities?.filter(e => e.entityType === eSummaryEntityType.securedParty);
  }

  getAssignors() {
    return this.result.entities?.filter(e => e.entityType === eSummaryEntityType.assignor);
  }

  getAssignees() {
    return this.result.entities?.filter(e => e.entityType === eSummaryEntityType.assignee);
  }

  getDealers() {
    return this.result.entities?.filter(e => e.entityType === eSummaryEntityType.dealer);
  }

  getOtherParties() {
    return this.result.entities?.filter(e => e.entityType === eSummaryEntityType.otherParty);
  }

  getUndeterminedParties() {
    return this.result.entities?.filter(e => e.entityType === eSummaryEntityType.undeterminedParty);
  }

  getSerialCollateral() {
    return this.result.entities?.filter(e => e.entityType === eSummaryEntityType.serialCollateral);
  }

  getGeneralCollateral() {
    return this.result.textBlocks?.filter(t => t.type === eSummaryTextBlockType.generalCollateral);
  }

  getAdditionalInformation() {
    return this.result.textBlocks?.filter(t => t.type === eSummaryTextBlockType.additionalInformation);
  }

  getAmendmentDetails() {
    return this.result.textBlocks?.filter(t => t.type === eSummaryTextBlockType.amendmentDetails);
  }

  onPagingSerialCollateral(e: PageEvent) {
    this.currentPageSize = e.pageSize;
    this.currentPageIndex = e.pageIndex;
    this.pageSerialCollateral();
  }

  onSerialNumberFilterChange(event: any) {
    const value = event.target.value;

    if (value) {
      this.serialCollateralFiltered = this.serialCollateral?.filter(e => e.identifier.includes(event.target.value)) ?? [];
    } else {
      this.serialCollateralFiltered = this.serialCollateral;
    }

    this.serialCollateralLength = this.serialCollateralFiltered.length;

    const maxPage = Math.floor(this.serialCollateralLength / this.currentPageSize);
    if (this.currentPageIndex >= maxPage) {
      this.currentPageIndex = maxPage;
    } else if (maxPage <= 0) {
      this.currentPageIndex = 0;
    }

    this.pageSerialCollateral();
  }

  pageSerialCollateral() {
    const start = this.currentPageIndex * this.currentPageSize;
    const end = start + this.currentPageSize;
    const page = this.serialCollateralFiltered.slice(start, end);
    const sliceSize = this.currentPageSize / 2;
    this.serialCollateralLeft = page.slice(0, sliceSize);
    this.serialCollateralRight = page.slice(sliceSize, this.currentPageSize);
  }

  shouldShowOntarioClassificationsSection() {
    if(!this.result.isChangeType) {
      return true;
    } else {
      return this.result.registrationType !== 'CSRA' &&
        (this.result.changeType === 'Amendment' ||
        this.result.changeType === 'Transfer Of Part Of The Collateral' ||
        this.result.changeType === 'Assignment Of Part Of The Collateral'); 
    }
  }
}
